import * as v from 'valibot';

const getValAtPath = (target: unknown, path: string[]): unknown =>
  path.reduce((result, key) => (result as Record<string, unknown>)?.[key], target);

const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Checks strict equality between two top-level object properties; uses the supplied message or a
 * generated field-name message.
 */
export const valuesEquivalent = <TInput extends Record<string, unknown>>(
  antecedentKey: string,
  dependentKey: string,
  message?: string
) => {
  const defaultMessage = `${capitalize(antecedentKey)} and ${dependentKey} must be equivalent`;

  return v.check((input: TInput) => {
    const antecedent = getValAtPath(input, [antecedentKey]);
    const dependent = getValAtPath(input, [dependentKey]);
    return antecedent === dependent;
  }, message || defaultMessage);
};

/**
 * Checks logical implication: the dependent condition must hold whenever the antecedent holds. Uses
 * the supplied message or the default implication message.
 */
export const conditionalCheck = <TInput extends Record<string, unknown>>(
  antecedentRequirement: (input: TInput) => boolean,
  dependentRequirement: (input: TInput) => boolean,
  message?: string
) => {
  const defaultMessage = 'Dependent condition must be true when antecedent condition is true';

  return v.check((input: TInput) => {
    const antecedentCondition = antecedentRequirement(input);
    const dependentCondition = dependentRequirement(input);
    return !antecedentCondition || dependentCondition; // !A || B is equivalent to A → B
  }, message || defaultMessage);
};
